import { format } from 'date-fns';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Border, Breakpoints, Color, Grid, Type } from '../../../app/StyleGuide';
import { SecondaryBadge } from '../../../components/Badge';
import { SmallWhiteButton } from '../../../components/Buttons';
import CalendarDateIcon from '../../../components/CalendarDateIcon';
import { InlineList } from '../../../components/Layout';
import { CustomizationIndicator } from '../../../components/SessionElements';
import useModal from '../../../hooks/useModal';
import windowService from '../../../services/windowService';
import { AgeCategory, BrandCode, LeaderTimelinesOverlay, Session, Timeline } from '../../curriculum';

const SessionList = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Grid._4};
  padding: 0;
  margin: 0;
`;

const SessionItemWrapper = styled.li<{ isThisWeek: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Grid._4};
  padding: ${Grid._3};
  color: ${Type.Color.medium};
  list-style: none;
  background: ${props => (props.isThisWeek ? Color.Gray._20 : Color.Gray._05)};
  border: 1px solid ${props => (props.isThisWeek ? Color.Secondary._80 : Color.Gray._10)};
  border-radius: ${Border.radius};
  position: relative;

  @media (min-width: ${Breakpoints.screen_sm}) {
    grid-template-columns: 1fr auto;
    gap: ${Grid._3};
    padding: ${Grid._4};
  }

  h3 {
    margin: 0;
    font-size: ${Type.Scale._3};
    font-weight: ${Type.Weight.semibold};
    color: ${Type.Color.dark};
  }

  &:hover {
    cursor: pointer;

    h3 {
      text-decoration: underline;
    }
    ${SmallWhiteButton} {
      background-color: ${Color.Gray._10};
    }
  }
`;

const CalendarAndDetailsWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${Grid._3};

  @media (min-width: ${Breakpoints.screen_sm}) {
    gap: ${Grid._4};
  }
`;

const SessionDetails = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${Grid._2};

  @media (min-width: ${Breakpoints.screen_sm}) {
    grid-template-columns: 1fr auto;
    margin-top: 0;
  }
`;

const SecondaryDetailsList = styled(InlineList)`
  li {
    font-size: ${Type.Scale._1};

    &:last-child {
      display: none;
    }

    @media (min-width: ${Breakpoints.screen_md}) {
      &:last-child {
        display: initial;
      }
    }
  }
`;

const CustomizationLabel = styled.span`
  margin-top: ${Grid._1};
  font-size: ${Type.Scale._05};

  i {
    font-size: inherit;
  }
`;

const ButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  place-content: center;
`;

const ThisWeekBadge = styled(SecondaryBadge)`
  position: absolute;
  top: -12px;
  right: ${Grid._6};
`;

type SessionItemProps = {
  session: Session;
  volumeName: string;
  onEditClick: () => void;
};

const SessionItem = ({ session, volumeName, onEditClick }: SessionItemProps) => {
  const hasCustomization = session.timelines.some(t => t.timelineCustomizationId);

  return (
    <SessionItemWrapper
      data-tracking-component="overview-session"
      data-tracking-id={session.name}
      isThisWeek={session.isThisWeek}
      onClick={onEditClick}
    >
      <CalendarAndDetailsWrapper>
        <CalendarDateIcon
          month={format(session.scheduledDate, 'MMM')}
          day={format(session.scheduledDate, 'd')}
          primary={session.isThisWeek}
        />

        <SessionDetails>
          <SecondaryDetailsList>
            <li>
              {volumeName}: Session {session.sessionNumber}
            </li>
            <li>Exp {format(session.availableThroughDate, 'MMM d')}</li>
          </SecondaryDetailsList>
          <h3>{session.name}</h3>
          {hasCustomization && (
            <CustomizationLabel>
              <CustomizationIndicator /> Customized
            </CustomizationLabel>
          )}
        </SessionDetails>
      </CalendarAndDetailsWrapper>

      <ButtonWrapper>
        <SmallWhiteButton onClick={onEditClick}>Edit</SmallWhiteButton>
      </ButtonWrapper>

      {session.isThisWeek && <ThisWeekBadge>This Week</ThisWeekBadge>}
    </SessionItemWrapper>
  );
};

type OverviewSessionsSectionProps = {
  brandCode: BrandCode;
  ageCategory: AgeCategory;
  volumeName: string;
  sessions: Session[];
};

export const OverviewSessionsSection = ({
  brandCode,
  ageCategory,
  volumeName,
  sessions: originalSessions,
}: OverviewSessionsSectionProps) => {
  const [sessions, setSessions] = useState(originalSessions || []);

  const handleSessionTimelinesUpdate = (sessionId: string, timelines: Timeline[]) => {
    setSessions(prevSessions =>
      prevSessions.map(session => (session.sessionId === sessionId ? { ...session, timelines } : session))
    );
  };

  const handleSessionEditClick = (session: Session) => {
    if (session.timelines.length) {
      return openModal('timelines', session);
    }
    windowService.redirectTo(
      `/manage-bible-study/schedule/${brandCode}/${ageCategory}/${session.curriculumId}/${session.sessionId}`
    );
  };

  const [modal, openModal] = useModal((type: 'timelines', payload: Session, dismissModal: () => void) => {
    if (type === 'timelines') {
      return (
        <LeaderTimelinesOverlay
          brandCode={brandCode}
          ageCategory={ageCategory}
          session={payload}
          updateSessionTimelines={handleSessionTimelinesUpdate}
          handleDismiss={dismissModal}
        />
      );
    }
  });

  return (
    <SessionList>
      {sessions.map(session => (
        <SessionItem
          key={session.sessionId}
          session={session}
          volumeName={volumeName}
          onEditClick={() => handleSessionEditClick(session)}
        />
      ))}
      {modal}
    </SessionList>
  );
};
